'use client';

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../../../../ui/button';
import { Label } from '../../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../ui/radio-group';
import CustomTextInput from '../../../../../components/resuable-ui/text-input';
import CustomTextarea from '../../../../../components/resuable-ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '../../../../../components/ui/dialog';
import { useCartAnalytics } from '../../../../../hooks/useCartAnalytics';
import { useMarkCartWithTransactionID, usePayment } from '../../../../../integrations/react_query/mutations';
import { useGetCart, useGetPaymentTypes } from '../../../../../integrations/react_query/queries';
import { useCompleteSales } from '../../../../../hooks/useCompleteSales';
import { useSubmitGuard } from '../../../../../hooks/useSubmitGuard';
import { EQuerykeys, ECart } from '../../../../../models/index';
import { handlePayment, calculateBalanceDue } from '../utils/paymentUtils';
import { PaymentTypes } from '../../../../../types/index';

type PaymentMethod = {
	label: string;
	id: string;
	value: string;
};

interface PaymentConfirmationProps {
	title?: string;
	methods: PaymentMethod[];
	defaultAmount: string | number;
	onConfirm: (data: PaymentConfirmationForm) => void;
	onClear?: () => void;
	showConfirmationModal: boolean;
	setShowConfirmationModal: (show: boolean) => void;
	paymentType: string;
}

const PaymentConfirmationSchema = z.object({
	method: z.string().nonempty('Please select a payment method'),
	amount: z.coerce.number().min(1, 'Amount must be at least 1'),
	note: z.string().max(100, 'Note must be at most 100 characters').optional(),
});

type PaymentConfirmationForm = z.infer<typeof PaymentConfirmationSchema>;

const PaymentConfirmation: React.FC<PaymentConfirmationProps> = ({
	title = 'Card Payment Confirmation',
	methods,
	defaultAmount,
	onConfirm,
	onClear,
	showConfirmationModal,
	setShowConfirmationModal,
	paymentType,
}) => {
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { isSubmitting },
	} = useForm<PaymentConfirmationForm, typeof PaymentConfirmationSchema>({
		resolver: zodResolver(PaymentConfirmationSchema) as any,
		defaultValues: {
			method: '',
			amount: 0,
			note: '',
		},
	});

	const derivedPaymentType =
		paymentType === PaymentTypes.card ? 'pos' : paymentType === PaymentTypes.transfer ? 'bank' : '';

	const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = useGetPaymentTypes([
		EQuerykeys.payment_types,
		derivedPaymentType,
	]);

	React.useEffect(() => {
		if (!paymentMethods || paymentMethods?.length <= 0) return;
		// setValue('method', paymentMethods?.[0]?._id);
	}, [isLoadingPaymentMethods, paymentMethods]);

	const composePaymentMethods =
		paymentMethods
			?.map((method: any) => ({
				label: method?.bank?.name || method?.terminalName,
				terminalIdentifier: method?.terminalIdentifier,
				accountNumber: method?.accountNumber,
				accountName: method?.accountName,
				provider: method?.provider?.name,
				id: method._id,
				value: method._id,
				type: method?.paymentType,
			}))
			?.filter((d: any) => d.type === derivedPaymentType) || [];

	const { data: cartItems } = useGetCart([EQuerykeys.cart, ECart.active]);
	const { completeTransaction, transactionId } = useCompleteSales();
	const { mutateAsync: markCartWithTransactionID } = useMarkCartWithTransactionID();
	const { mutateAsync: makePayment } = usePayment();

	const { payableAmount } = useCartAnalytics(cartItems?.products);

	const [showOverpayModal, setShowOverpayModal] = React.useState(false);
	const [pendingData, setPendingData] = React.useState<PaymentConfirmationForm | null>(null);

	const { isSubmitting: isConfirmingOverpay, runGuarded: runOverpayConfirm } = useSubmitGuard();

	const onSubmit = async (data: PaymentConfirmationForm) => {
		if (!cartItems) return;

		const paymentProvider = paymentMethods?.find((method: any) => method?._id === data?.method) ?? {};

		const balanceDueAfter = calculateBalanceDue(+payableAmount, cartItems.payments, Number(data.amount));

		if (balanceDueAfter <= 0) {
			setPendingData({ ...data });
			setShowOverpayModal(true);
			return;
		}

		await handlePayment({
			amountPaid: data.amount,
			note: data.note,
			paymentType: paymentType as PaymentTypes,
			paymentsHistory: cartItems.payments,
			payableAmount: +payableAmount,
			transactionId,
			onPayment: makePayment as unknown as any,
			markCartWithTransactionID,
			completeTransaction,
			onSuccess: () => {
				setShowConfirmationModal(false);
			},
			paymentProvider,
		});
	};

	const noteValue = watch('note') || '';
	const amountValue = watch('amount') || 0;

	const balanceDue = React.useMemo(() => {
		if (!cartItems) return 0;
		return calculateBalanceDue(+payableAmount, cartItems.payments, Number(amountValue));
	}, [cartItems, amountValue, payableAmount]);

	return (
		<>
			{showOverpayModal && (
				<Dialog open={showOverpayModal} onOpenChange={setShowOverpayModal}>
					<DialogContent className="bg-white p-6 max-w-md text-center">
						<DialogTitle className="text-lg font-semibold">Confirm Payment</DialogTitle>
						<p className="mt-4 text-gray-700">
							The amount for the cart items has been
							{balanceDue < 0 ? ' overpaid.' : ' fully paid.'} <br />
							Click "Yes" to proceed if you confirm this.
						</p>
						<p className="mt-2 font-bold text-sm text-gray-600">Note: The sale will be recorded automatically.</p>

						<div className="flex flex-row justify-center gap-4 mt-6">
							<Button
								className="border border-[#E2E8F0] bg-[#FEFBFF] py-6 text-[#718096]"
								type="button"
								variant="outline"
								onClick={() => setShowOverpayModal(false)}
							>
								No
							</Button>
							<Button
								className='className="flex-1 bg-[#A63494] py-6 font-semibold text-xl text-center text-white'
								type="button"
								disabled={isConfirmingOverpay}
								onClick={() => {
									if (!pendingData || !cartItems) return;
									const paymentProvider =
										paymentMethods?.find((method: any) => method?._id === pendingData?.method) ?? {};

									void runOverpayConfirm(() =>
										handlePayment({
											amountPaid: pendingData.amount,
											note: pendingData.note,
											paymentType: paymentType as PaymentTypes,
											paymentsHistory: cartItems.payments,
											payableAmount: +payableAmount,
											transactionId,
											onPayment: makePayment as unknown as any,
											markCartWithTransactionID,
											completeTransaction,
											onSuccess: () => {
												setShowOverpayModal(false);
												setShowConfirmationModal(false);
											},
											paymentProvider,
										})
									);
								}}
							>
								Yes
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}

			{/* Desktop Modal with Dialog */}
			<Dialog
				open={showConfirmationModal && window.innerWidth >= 768}
				onOpenChange={(v) => setShowConfirmationModal(v)}
			>
				<DialogContent className="!min-w-[850px] bg-white px-3 gap-0 sm:w-full">
					<DialogTitle className="flex items-center">
						<h3 className="text-lg uppercase text-[20px] font-[700]">{title}</h3>
					</DialogTitle>

					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="grid md:grid-cols-2 gap-10 mt-3">
							<div className="space-y-4 max-h-[50vh] overflow-y-auto">
								<Label className="text-[#718096] text-[12px]">Select Payment Method</Label>
								<Controller
									name="method"
									control={control}
									render={({ field, fieldState }) => (
										<>
											{fieldState.error && <p className="text-red-500 text-sm">{fieldState.error.message}</p>}
											<RadioGroup value={field.value} onValueChange={field.onChange}>
												{composePaymentMethods?.map((method: any) => (
													<label
														key={method.value}
														htmlFor={method.value}
														className={`flex items-center justify-between p-2 border rounded-md cursor-pointer transition
              ${field.value === method.value ? 'border-primary bg-purple-50' : 'border-muted hover:border-primary/50'}
            `}
													>
														<div className="space-y-1">
															<div className="text-[14px] font-[700]">{method.label}</div>

															{method?.terminalIdentifier && (
																<div className="text-[13px] font-[400] text-muted-foreground">
																	{method.terminalIdentifier}
																</div>
															)}

															{method?.accountName && method?.accountNumber && (
																<div className="flex items-center gap-x-3 text-[13px] font-[400]">
																	<div>{method.accountName}</div>
																	<div className="w-[1px] h-[10px] bg-[#DD6B20]"></div>
																	<div>{method.accountNumber}</div>
																</div>
															)}
														</div>

														<RadioGroupItem id={method.value} value={method.value} />
													</label>
												))}
											</RadioGroup>
										</>
									)}
								/>
							</div>

							<div className="space-y-4">
								<div>
									<div className="flex items-center justify-between mb-2 text-[#353535] font-[600] text-[12px]">
										<Label htmlFor="amount">
											Balance due -{' '}
											<span className={balanceDue < 0 ? 'text-red-500' : balanceDue === 0 ? 'text-green-600' : ''}>
												₦{Math.abs(balanceDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
												{balanceDue < 0 && ' (Overpaid)'}
											</span>
										</Label>

										<Label htmlFor="amount">Total - ₦{Number(defaultAmount).toLocaleString()}</Label>
									</div>

									<Controller
										name="amount"
										control={control}
										render={({ field, fieldState }) => (
											<CustomTextInput
												id="amount"
												{...field}
												onChange={(e) => field.onChange(e.target.value)}
												className="mt-1 h-[45px]"
												placeholder="Enter amount paid"
												error={fieldState.error}
											/>
										)}
									/>

									<Button
										disabled={balanceDue <= 0}
										onClick={() => {
											if (balanceDue > 0) {
												setValue('amount', balanceDue + +amountValue);
											}
										}}
										type="button"
										className="w-full mt-4 font-semibold text-xl text-center bg-[#F8EAFF] border border-[#DBB8EE] text-[#A63494] py-6 rounded"
									>
										₦ {Number(balanceDue + +amountValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
									</Button>
								</div>

								<div className="mt-5">
									<Label className="mb-2" htmlFor="note">
										Note (Optional)
									</Label>
									<Controller
										name="note"
										control={control}
										render={({ field, fieldState }) => (
											<CustomTextarea
												label=""
												id="note"
												{...field}
												placeholder="Write a note..."
												maxLength={100}
												error={fieldState.error}
												rows={3}
											/>
										)}
									/>
									<div className="text-right text-xs text-muted-foreground">{noteValue.length}/100</div>
								</div>
							</div>
						</div>

						<div className="grid md:grid-cols-2 gap-6 mt-6">
							<Button
								className="max-w-fit border border-[#E2E8F0] bg-[#FEFBFF] py-6 px-6 text-[#718096]"
								type="button"
								variant="outline"
								onClick={onClear}
							>
								Clear
							</Button>

							<Button
								className="flex-1 bg-[#A63494] py-6 font-semibold text-xl text-center text-white"
								type="submit"
								disabled={isSubmitting}
							>
								OK
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>

			{/* Mobile Modal Content (rendered directly by ModalV1) */}
			{!showConfirmationModal ? null : (
				<form onSubmit={handleSubmit(onSubmit)} className="md:hidden px-4 pb-6">
					<div className="space-y-5">
						{/* Payment Methods Section - Horizontal Scrollable on Mobile */}
						<div className="space-y-3">
							<Label className="text-[#718096] text-[12px] font-semibold">Select Payment Method</Label>
							<Controller
								name="method"
								control={control}
								render={({ field, fieldState }) => (
									<>
										{fieldState.error && <p className="text-red-500 text-sm">{fieldState.error.message}</p>}
										<div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
											<RadioGroup value={field.value} onValueChange={field.onChange} className="flex gap-2">
												{composePaymentMethods?.map((method: any) => (
													<label
														key={method.value}
														htmlFor={method.value}
														className={`flex-shrink-0 snap-center flex flex-col items-start justify-between p-3 border rounded-lg cursor-pointer transition min-w-[200px]
              ${field.value === method.value ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}
            `}
													>
														<div className="space-y-1 w-full">
															<div className="text-[12px] font-[600] text-gray-800">{method.label}</div>

															{method?.terminalIdentifier && (
																<div className="text-[11px] font-[400] text-gray-500">{method.terminalIdentifier}</div>
															)}

															{method?.accountName && method?.accountNumber && (
																<div className="flex items-center gap-x-1 text-[10px] font-[400] text-gray-500">
																	<div className="truncate">{method.accountName}</div>
																	<div className="w-[1px] h-[6px] bg-[#DD6B20] flex-shrink-0"></div>
																	<div className="truncate">{method.accountNumber}</div>
																</div>
															)}
														</div>

														<RadioGroupItem id={method.value} value={method.value} className="mt-2 flex-shrink-0" />
													</label>
												))}
											</RadioGroup>
										</div>
									</>
								)}
							/>
						</div>

						{/* Amount Section */}
						<div className="space-y-3 border-t pt-4">
							<div className="flex items-center justify-between">
								<Label htmlFor="amount" className="text-[#718096] text-[12px]">
									Balance due
								</Label>
								<span
									className={`text-[12px] font-semibold ${balanceDue < 0 ? 'text-red-500' : balanceDue === 0 ? 'text-green-600' : 'text-gray-600'}`}
								>
									₦{Math.abs(balanceDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
									{balanceDue < 0 && ' (Overpaid)'}
								</span>
							</div>

							<div className="flex items-center justify-between text-[11px] text-gray-500 mb-2">
								<span>Total to pay</span>
								<span className="font-semibold text-gray-700">₦{Number(defaultAmount).toLocaleString()}</span>
							</div>

							<Controller
								name="amount"
								control={control}
								render={({ field, fieldState }) => (
									<CustomTextInput
										id="amount"
										{...field}
										onChange={(e) => field.onChange(e.target.value)}
										className="mt-1 h-[44px] text-sm"
										placeholder="Enter amount paid"
										error={fieldState.error}
									/>
								)}
							/>

							<Button
								disabled={balanceDue <= 0}
								onClick={() => {
									if (balanceDue > 0) {
										setValue('amount', balanceDue + +amountValue);
									}
								}}
								type="button"
								className="w-full font-semibold text-sm text-center bg-[#F8EAFF] border border-[#DBB8EE] text-[#A63494] py-3 rounded"
							>
								₦ {Number(balanceDue + +amountValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
							</Button>
						</div>

						{/* Note Section */}
						<div className="space-y-2 border-t pt-4">
							<Label className="text-[12px] text-gray-700 font-semibold" htmlFor="note">
								Note (Optional)
							</Label>
							<Controller
								name="note"
								control={control}
								render={({ field, fieldState }) => (
									<CustomTextarea
										label=""
										id="note"
										{...field}
										placeholder="Write a note..."
										maxLength={100}
										error={fieldState.error}
										rows={3}
										className="text-sm"
									/>
								)}
							/>
							<div className="text-right text-xs text-gray-500">{noteValue.length}/100</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="grid grid-cols-2 gap-3 mt-6">
						<Button
							className="border border-[#E2E8F0] bg-[#FEFBFF] py-3 px-4 text-[#718096] text-sm font-medium"
							type="button"
							variant="outline"
							onClick={onClear}
						>
							Clear
						</Button>

						<Button
							className="bg-[#A63494] py-3 px-4 font-semibold text-sm text-center text-white rounded"
							type="submit"
							disabled={isSubmitting}
						>
							OK
						</Button>
					</div>
				</form>
			)}
		</>
	);
};

export default PaymentConfirmation;
